import type React from 'react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { MenuOptions } from './menu-wrapper';
import { useOnClickOutside } from '../../hooks/use-on-click-outside';
import { usePrevious } from '../../hooks/use-previous';
import { Key } from '../../utils/key';

const supportedKeys: string[] = [Key.ArrowUp, Key.ArrowDown, Key.ArrowLeft, Key.ArrowRight, Key.Escape];

type MenuNavigationProps = {
  menuOptions: MenuOptions;
  wrapperRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  buttonName: string;
  isOpen: boolean;
};

/**
 * Implements the navigation logic for the player menus, including accessibility and keyboard navigation.
 *
 * @param menuOptions - The menu options to navigate through.
 * @param isOpen - Whether the menu is open or not.
 * @param onClose - The function to close the menu.
 * @param buttonName - The name of the button that opens the menu.
 * @param wrapperRef - The reference to the menu wrapper element.
 *
 * @returns
 */
export const useMenuNavigation = ({ menuOptions, isOpen, onClose, buttonName, wrapperRef }: MenuNavigationProps) => {
  const [currentScreen, setCurrentScreen] = useState(menuOptions.id);
  const previousScreen = usePrevious(currentScreen);
  const parentScreen = useMemo(() => getParentScreen(currentScreen, menuOptions), [currentScreen, menuOptions]);
  const currentScreenData = useMemo(() => getCurrentScreen(currentScreen, menuOptions), [currentScreen, menuOptions]);

  const wasOpen = usePrevious(isOpen);

  const onGoBack = useCallback(() => {
    if (parentScreen) {
      setCurrentScreen(parentScreen);
    } else {
      onClose();

      // Focus on the button that opened the menu
      (document.querySelector(`[name="${buttonName}"]`) as HTMLButtonElement)?.focus();
    }
  }, [parentScreen, onClose, buttonName]);

  useOnClickOutside(wrapperRef, onClose, buttonName, isOpen);

  // Reset menu navigation to the root when the menu is closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentScreen(menuOptions.id);
    }
  }, [isOpen, menuOptions.id]);

  // Handle focus
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleFocus = () => {
      const currentValue = currentScreenData?.value?.toString() || currentScreenData?.defaultValue?.toString();

      // Try to focus on the previous screen
      (previousScreen && focusOnSpecificButton(wrapperRef, previousScreen)) ||
        // Try to focus on the current selected value
        (currentValue && focusOnSpecificButton(wrapperRef, currentValue)) ||
        // if all else fails, focus on the first button
        focusOnFirstMenuItem(wrapperRef) ||
        // If no buttons are found, focus on the wrapper
        wrapperRef.current?.focus();
    };

    if (!wasOpen && isOpen) {
      // If the menu just opened, also wait for transitions to finish before focusing
      const wrapperElement = wrapperRef.current;
      if (wrapperElement) {
        // Ensure focus is set even if transitions are disabled
        handleFocus();

        const onTransitionEnd = (event: TransitionEvent) => {
          if (event.target === wrapperElement) {
            handleFocus();
            wrapperElement.removeEventListener('transitionend', onTransitionEnd);
          }
        };

        wrapperElement.addEventListener('transitionend', onTransitionEnd);

        return () => {
          wrapperElement?.removeEventListener('transitionend', onTransitionEnd);
        };
      }
    }

    handleFocus();
  }, [currentScreenData?.defaultValue, currentScreenData?.value, isOpen, previousScreen, wasOpen, wrapperRef]);

  // Handle keyboard events to implement keyboard navigation
  useEffect(() => {
    const wrapperElement = wrapperRef.current;

    if (!wrapperElement) return;

    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case Key.ArrowUp: {
          const sibling = document.activeElement?.previousElementSibling as HTMLElement;
          if (sibling?.getAttribute('role') === 'menuitem') {
            sibling.focus();
          }
          break;
        }

        case Key.ArrowDown: {
          const sibling = document.activeElement?.nextElementSibling as HTMLElement;
          if (sibling?.getAttribute('role') === 'menuitem') {
            sibling.focus();
          }
          break;
        }

        case Key.ArrowLeft:
          onGoBack();
          break;

        case Key.ArrowRight: {
          const goToAttribute = document.activeElement?.getAttribute('data-goto');
          if (typeof goToAttribute === 'string') {
            const option = currentScreenData?.options?.find((option) => option.id === goToAttribute);
            if (option?.options?.length) {
              setCurrentScreen(option.id);
            }
          }
          break;
        }

        case Key.Escape:
          onClose();

          // Focus on the button that opened the menu
          (document.querySelector(`[name="${buttonName}"]`) as HTMLButtonElement)?.focus();
          break;
      }

      if (supportedKeys.includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    wrapperElement.addEventListener('keydown', onKeyDown);

    return () => {
      wrapperElement.removeEventListener('keydown', onKeyDown);
    };
  }, [buttonName, currentScreen, currentScreenData?.options, onClose, onGoBack, wrapperRef]);

  return {
    currentScreen,
    setCurrentScreen,
    currentScreenData,
    parentScreen,
    onGoBack,
  };
};

function getCurrentScreen(currentScreen: string, menuOptions: MenuOptions): MenuOptions | undefined {
  if (currentScreen === menuOptions.id) return menuOptions;

  for (const option of menuOptions.options || []) {
    if (option.id === currentScreen) return option;

    const options = getCurrentScreen(currentScreen, option);
    if (options) return options;
  }

  return undefined;
}

function getParentScreen(currentScreen: string, menuOptions: MenuOptions): string | undefined {
  if (currentScreen === menuOptions.id) return undefined;

  for (const option of menuOptions.options || []) {
    if (option.id === currentScreen) return menuOptions.id;

    if (option.options) {
      const parentScreen = getParentScreen(currentScreen, option);
      if (parentScreen) return parentScreen;
    }
  }

  return undefined;
}

function focusOnFirstMenuItem(wrapperRef: React.RefObject<HTMLDivElement>) {
  const button = wrapperRef.current?.querySelectorAll('button[role="menuitem"]')?.[0] as HTMLButtonElement;
  button?.focus();
  return button;
}

function focusOnSpecificButton(wrapperRef: React.RefObject<HTMLDivElement>, submenu: string | undefined) {
  const button = wrapperRef.current?.querySelector(
    `[data-goto="${submenu?.replaceAll(`"`, '\\"')}"]`
  ) as HTMLButtonElement;
  button?.focus();
  return button;
}
